// Wraps a printable document with a Print button. Printing goes through
// react-to-print (a hidden iframe + window.print()) so only the document is
// printed, not the admin shell or the drawer around it.

import { useRef, type ReactNode } from 'react';
import { Button, Stack } from '@mui/material';
import { Printer } from 'iconsax-reactjs';
import { useReactToPrint } from 'react-to-print';

import { t } from '../../i18n';
import { documentCss, invoicePageStyle, receiptPageStyle } from './documentStyles';

export function PrintableDocument({
  children,
  title,
  variant = 'invoice',
  actions,
  autoPrint = false
}: {
  children: ReactNode;
  title?: string;
  variant?: 'invoice' | 'receipt';
  // Extra buttons rendered next to Print (not printed).
  actions?: ReactNode;
  autoPrint?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const print = useReactToPrint({
    contentRef,
    documentTitle: title,
    pageStyle: variant === 'receipt' ? receiptPageStyle : invoicePageStyle
  });
  const printedOnce = useRef(false);

  return (
    <Stack spacing={1.5}>
      <div
        ref={(node) => {
          contentRef.current = node;
          if (node && autoPrint && !printedOnce.current) {
            printedOnce.current = true;
            window.setTimeout(() => print(), 300);
          }
        }}
      >
        <style>{documentCss}</style>
        {children}
      </div>
      <Stack direction="row" spacing={1} justifyContent="center" className="vd-no-print" flexWrap="wrap" useFlexGap>
        <Button variant="contained" startIcon={<Printer size={16} />} onClick={() => print()}>
          {t('doc.print')}
        </Button>
        {actions}
      </Stack>
    </Stack>
  );
}
